'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { Loader } from '@skop-app/lib-front'
import { useTranslations } from 'next-intl'

import { useReview } from '@/context'
import { type ReviewsFiltersType, useReviewsData } from '@/hooks/useReviewsData'
import { ReviewDrawerThread } from '@/types'
import { getReviewProjectId } from '@/utils/reviews'

import { ReviewsFilters } from './Filters'
import { ElementCard } from './Element'

export const Divider = () => (
  <div className="w-full py-2">
    <div className="h-px bg-neutral-200" />
  </div>
)

function NoResult() {
  const t = useTranslations()

  return (
    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
      <p className="whitespace-pre-line text-sm text-slate-500">
        {t('Review.nothing')}
      </p>
    </div>
  )
}

export const ReviewsDrawer = () => {
  const {
    selectedReviewId,
    pendingRevealReviewId,
    selectReviewThread,
    clearPendingRevealReviewId,
    findThreadByReviewId,
  } = useReview()
  const reviewRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const [filters, setFilters] = useState<ReviewsFiltersType>({
    activeTab: 'allComments',
    activeStatus: 'open',
    activeProject: 'allProjects',
  })

  const { projects, filteredData: data, isLoading } = useReviewsData(filters)

  useEffect(() => {
    if (!selectedReviewId || pendingRevealReviewId !== selectedReviewId) {
      return
    }

    const isSelectedThreadVisible = data.some((element) =>
      element.threads.some((thread) => thread.reviewId === selectedReviewId),
    )

    if (isSelectedThreadVisible) return

    const selectedReview = findThreadByReviewId(selectedReviewId)
    if (!selectedReview) return

    const nextProject = getReviewProjectId(selectedReview) || 'allProjects'

    const timeoutId = window.setTimeout(() => {
      setFilters((prev) => {
        const nextFilters: ReviewsFiltersType = {
          activeTab: 'allComments',
          activeStatus: 'allStatuses',
          activeProject: nextProject,
        }

        return prev.activeTab === nextFilters.activeTab &&
          prev.activeStatus === nextFilters.activeStatus &&
          prev.activeProject === nextFilters.activeProject
          ? prev
          : nextFilters
      })
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [data, findThreadByReviewId, pendingRevealReviewId, selectedReviewId])

  useEffect(() => {
    if (!pendingRevealReviewId) return

    const isPendingThreadVisible = data.some((element) =>
      element.threads.some(
        (thread) => thread.reviewId === pendingRevealReviewId,
      ),
    )

    if (isPendingThreadVisible) {
      clearPendingRevealReviewId()
    }
  }, [clearPendingRevealReviewId, data, pendingRevealReviewId])

  useEffect(() => {
    if (!selectedReviewId) return

    reviewRefs.current[selectedReviewId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [data, selectedReviewId])

  const setElementRef = useCallback(
    (reviewId: string) => (el: HTMLDivElement | null) => {
      reviewRefs.current[reviewId] = el
    },
    [],
  )

  const handleSelectThread = useCallback(
    (thread: ReviewDrawerThread) => {
      selectReviewThread(thread.reviewId)
    },
    [selectReviewThread],
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <ReviewsFilters
        projects={projects}
        filters={filters}
        setFilters={setFilters}
      />

      {isLoading ? (
        <div className="flex h-52 w-full items-center justify-center">
          <Loader />
        </div>
      ) : data.length ? (
        <div className="flex flex-col gap-4 pb-4">
          {data.map((element) => (
            <ElementCard
              key={element.id}
              element={element}
              selectedReviewId={selectedReviewId}
              onSelectThread={handleSelectThread}
              setReviewRef={setElementRef}
            />
          ))}
        </div>
      ) : (
        <NoResult />
      )}
    </div>
  )
}
